import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import type { ActorDatabase, ActorDatabaseHandle } from "@-/dev.improve.haniverse.database.actor";
import type { FindFirstConfig } from "@-/dev.improve.haniverse.lib.database";
import { useActorTableChangeVersion } from "@-/dev.improve.haniverse.lib.database.change.actor";
import { withWriteClockReadGuard } from "@-/dev.improve.haniverse.lib.database.change";
import type { ImportMetaLocation } from "@-/dev.improve.haniverse.lib.logger";
import type { QueryClient, QueryFunction } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { stripRelationalConfigColumns } from "#strip-relational-config-columns";
import type { ActorColumnsAgnosticConfig, ActorQueryTable, ActorRelationalRow } from "#types";

// `location` is the caller's `import.meta.location`, threaded in via
// `actorQueries(import.meta.location)`, so `db(location)` attributes query logs
// to the read's origin instead of this factory file. `null` opts out of logging.
export function createActorFindFirstQuery<TTable extends ActorQueryTable>(
  location: ImportMetaLocation,
  table: TTable,
) {
  type FindFirstQueryConfig = FindFirstConfig<ActorDatabaseHandle, TTable>;
  // The joined row for a specific caller config `Cfg` — reconstructed from the
  // actor schema's relational config so `config.with` relations are reflected
  // in the type. `config.where`/… don't change the shape (and `columns` is
  // banned — rows are always full-width), so a plain config that isn't
  // statically known collapses to the base row.
  type RowFor<Cfg extends true | Record<string, unknown>> = ActorRelationalRow<TTable, Cfg>;

  type FindFirstQueryKey<
    WithConfig extends FindFirstQueryConfig & ActorColumnsAgnosticConfig,
    Config extends FindFirstQueryConfig & ActorColumnsAgnosticConfig,
  > = readonly ["actorQuery", TTable, "findFirst", WithConfig, Config, { db: ActorDatabase }];

  // `useGetQueryKey` binds the change-version subscription once (at the top
  // level of a hook) from `withConfig`, then the returned getter builds a key
  // from a per-lookup `config` supplied later — inside a callback, for many
  // different `where`s. That late binding lets prefetch/mutation code subscribe
  // with a static `with` while keying each individual read.
  //
  // Both configs are column-stripped HERE, the single choke point every key
  // flows through: the query functions read their fetch config back off the key,
  // so stripping at key-build time makes cache entries column-agnostic.
  function useGetQueryKey<
    const WithConfig extends FindFirstQueryConfig & ActorColumnsAgnosticConfig,
  >(withConfig: WithConfig) {
    const [db] = useContextSelectors(ActorDatabaseContext, (context) => context.db);
    const strippedWithConfig = stripRelationalConfigColumns(withConfig);
    const withTableChangeVersion = useActorTableChangeVersion(table, withConfig);

    return <const Config extends FindFirstQueryConfig & ActorColumnsAgnosticConfig>(
      config: Config,
    ): FindFirstQueryKey<WithConfig, Config> =>
      withTableChangeVersion([
        "actorQuery",
        table,
        "findFirst",
        strippedWithConfig,
        stripRelationalConfigColumns(config),
        { db },
      ] as const);
  }

  async function queryFn({
    queryKey,
  }: {
    client?: QueryClient;
    queryKey: ReturnType<ReturnType<typeof useGetQueryKey>>;
    // The pair form reads its config at runtime from the key, so its config type
    // isn't statically known — resolve to the base (relation-less) row.
  }): Promise<RowFor<Record<string, never>> | null> {
    const withConfig = queryKey[3];
    const config = queryKey[4];
    const { db } = queryKey[5];
    const queryTable = db(location).query[table];
    return (
      /* eslint-disable typescript/no-unsafe-type-assertion -- `table` and `config` are paired in the query key; drizzle cannot recover the per-table `KnownKeysOnly` generic through the factory parameter */
      ((await queryTable.findFirst({ ...withConfig, ...config } as Parameters<
        typeof queryTable.findFirst
      >[0])) as RowFor<Record<string, never>> | undefined) ??
      /* eslint-enable typescript/no-unsafe-type-assertion -- Restore checked assertions after this documented bridge. */ null
    );
  }

  /**
   * Builds query options from a render-bound key getter and a lookup-specific
   * config. `useGetQueryKey` captures hooks, the database, and the static
   * relation shape once; this pure function is safe to call later from
   * navigator/prefetch callbacks.
   */
  // See `create-actor-find-many-query.ts` — every actor read, including the
  // standalone `queryFn` used in `fetchQuery` composition, goes through the guard.
  const guardedQueryFn = withWriteClockReadGuard(queryFn);

  function getQueryOptions<
    const WithConfig extends FindFirstQueryConfig & ActorColumnsAgnosticConfig,
    const Config extends FindFirstQueryConfig & ActorColumnsAgnosticConfig,
  >(getQueryKey: (config: Config) => FindFirstQueryKey<WithConfig, Config>, config: Config) {
    const queryKey = getQueryKey(config);
    const typedQueryFn: QueryFunction<
      RowFor<WithConfig & Config> | null,
      typeof queryKey
    > = async ({ client, queryKey: queryFnQueryKey }) =>
      // The key getter carries the precise static `with` and lookup config types.
      // `queryFn` intentionally exposes the relation-agnostic base type because
      // it can also be called directly without a typed options builder.
      /* eslint-disable typescript/no-unsafe-type-assertion -- refined by the paired key getter above */
      (await guardedQueryFn({ client, queryKey: queryFnQueryKey })) as RowFor<
        WithConfig & Config
      > | null;
    /* eslint-enable typescript/no-unsafe-type-assertion -- Restore checked assertions after this documented bridge. */

    return { queryFn: typedQueryFn, queryKey };
  }

  // Single-shot entry point. Capturing the literal `Cfg` lets the return type
  // reflect `Cfg["with"]` joins. The split pair above remains for prefetch and
  // mutation callers that must defer `where`.
  function useQueryOptions<const Cfg extends FindFirstQueryConfig & ActorColumnsAgnosticConfig>(
    config: Cfg,
  ) {
    const getQueryKey = useGetQueryKey(config);
    const { queryFn } = getQueryOptions(getQueryKey, config);

    return {
      queryFn,
      get queryKey() {
        return getQueryKey(config);
      },
    };
  }

  return { getQueryOptions, queryFn: guardedQueryFn, useGetQueryKey, useQueryOptions };
}
